// src/prototype/components/EAConfidencePill.tsx
// ============================================================
// EA CONFIDENCE PILL
// Shows confidence level for inferred or derived context attributes
// High = strong signal, Medium = moderate signal, Low = weak signal
// ============================================================

import React from 'react';
import { CircleCheck, CircleMinus, CircleAlert } from 'lucide-react';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

interface EAConfidencePillProps {
  level: ConfidenceLevel;
  showLabel?: boolean;
  className?: string;
}

const confidenceConfig: Record<ConfidenceLevel, {
  label: string;
  icon: React.ElementType;
  bgColor: string;
  textColor: string;
  borderColor: string;
  tooltip: string;
}> = {
  high: {
    label: 'High',
    icon: CircleCheck,
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    tooltip: 'High confidence - based on strong signals',
  },
  medium: {
    label: 'Medium',
    icon: CircleMinus,
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
    tooltip: 'Medium confidence - based on moderate signals',
  },
  low: {
    label: 'Low',
    icon: CircleAlert,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    tooltip: 'Low confidence - may need verification',
  },
};

export function EAConfidencePill({ level, showLabel = true, className = '' }: EAConfidencePillProps) {
  const config = confidenceConfig[level];
  const Icon = config.icon;

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold
        border ${config.bgColor} ${config.textColor} ${config.borderColor}
        ${className}
      `}
      title={config.tooltip}
    >
      <Icon className="w-3 h-3" />
      {showLabel && config.label}
    </span>
  );
}

export default EAConfidencePill;